import { useState } from 'react'
import { DEFAULT_CONSENT_MESSAGE } from '@assessments/shared'
import { CheckIcon } from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { useSession } from '../auth/SessionContext.js'
import { canEditAssessment } from '../auth/permissions.js'
import { useTerms } from './useTerms.js'
import { useAcceptTerms } from './useAcceptTerms.js'

export interface AcceptTermsProps {
  organizationSurveyId: string
  /** Called after acceptance is recorded (navigate back to the overview). */
  onAccepted?: () => void
  /** Return route, offered only once accepted — an outstanding gate stays a gate. */
  onBack?: () => void
}

export function AcceptTerms({ organizationSurveyId, onAccepted, onBack }: AcceptTermsProps) {
  const { data, loading, error, reload } = useTerms(organizationSurveyId)
  const { session } = useSession()
  const canEdit = canEditAssessment(session?.user.roles)

  // Never pre-set — resets to false on every mount of this screen.
  const [consented, setConsented] = useState(false)

  const {
    status,
    error: submitError,
    accept,
  } = useAcceptTerms(organizationSurveyId, {
    onSuccess: onAccepted,
    // A version change (409) invalidates the consent and reloads the new terms.
    onChanged: () => {
      setConsented(false)
      reload()
    },
  })

  if (loading) {
    return <main className="min-w-0 p-1 text-sm text-subtle">Loading terms…</main>
  }
  if (error || !data) {
    return (
      <main className="min-w-0 p-1">
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-subtle">
          Couldn’t load the terms. Please try again.
        </div>
      </main>
    )
  }

  const { content, acceptance } = data
  const consentLabel = content.consentMessage ?? DEFAULT_CONSENT_MESSAGE
  const submitting = status === 'submitting'

  const documentLink = content.documentUrl && (
    <p className="mt-4 text-sm">
      <a
        className="font-semibold text-primary underline"
        href={content.documentUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        View the Terms of Use document
      </a>
    </p>
  )

  if (acceptance.accepted) {
    return (
      <main className="min-w-0">
        <div className="mb-6 flex flex-wrap items-center gap-3 border-b border-border pb-4">
          {onBack && (
            <nav aria-label="Breadcrumb" className="mr-auto text-sm">
              <button
                type="button"
                onClick={onBack}
                className="font-semibold text-link underline-offset-2 hover:text-link-hover hover:underline"
              >
                Back to overview
              </button>
            </nav>
          )}
          <span className="ml-auto flex items-center gap-2 text-sm font-semibold text-body">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-success">
              <CheckIcon className="text-success-on" />
            </span>
            {acceptance.acceptedByName ? `Accepted by ${acceptance.acceptedByName}` : 'Accepted'}
          </span>
        </div>

        <h1 className="mb-4 text-[24px] font-semibold tracking-tight text-subtle">
          {content.title}
        </h1>

        <Tooltip content="The Terms of Use are accepted and cannot be changed.">
          {(target) => (
            <>
              <div className="rounded-lg border border-border bg-surface p-6">
                {content.body && (
                  <div
                    data-testid="terms-body"
                    tabIndex={0}
                    className="prose max-h-[60vh] max-w-none overflow-y-auto text-sm text-subtle"
                    dangerouslySetInnerHTML={{ __html: content.body }}
                    {...target}
                  />
                )}
                {documentLink}
              </div>

              <label className="mt-5 flex items-center gap-2 text-sm text-subtle">
                <input type="checkbox" checked disabled readOnly {...target} />
                {consentLabel}
              </label>
            </>
          )}
        </Tooltip>
      </main>
    )
  }

  return (
    <main className="min-w-0">
      <h1 className="mb-4 text-[24px] font-semibold tracking-tight text-body">{content.title}</h1>

      <div className="rounded-lg border border-border bg-surface p-6 shadow-card">
        {content.body && (
          // Bounded so long terms never push the consent control and accept action out
          // of view; tabIndex makes it keyboard-scrollable.
          <div
            data-testid="terms-body"
            tabIndex={0}
            className="prose max-h-[60vh] max-w-none overflow-y-auto text-sm text-body"
            dangerouslySetInnerHTML={{ __html: content.body }}
          />
        )}
        {documentLink}
      </div>

      {status === 'changed' && (
        <p className="mt-4 text-sm text-subtle" role="alert">
          {submitError ?? 'The terms have changed. Please review and accept the new version.'}
        </p>
      )}

      {canEdit ? (
        <div className="mt-5">
          <label className="flex items-center gap-2 text-sm text-body">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              disabled={submitting}
            />
            {consentLabel}
          </label>

          <button
            type="button"
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-on disabled:opacity-50"
            disabled={!consented || submitting}
            onClick={() => accept(acceptance.version)}
          >
            {submitting ? 'Accepting…' : 'Accept & Continue'}
          </button>

          {status === 'error' && (
            <p className="mt-3 text-sm text-subtle" role="alert">
              {submitError ?? 'Couldn’t record your acceptance. Please try again.'}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-5 text-sm text-subtle">
          You have view-only access. A company administrator must accept these terms before the
          application can begin.
        </p>
      )}
    </main>
  )
}
